import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useCallback,
} from "react";
import useFetchAllResources from "../../../hooks/useFetchAllResources";
import { Autocomplete, TextField } from "@mui/material";

interface IAutoCompleteResourceList {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handelOnChangeResource: (event: ChangeEvent<any>, value: any) => void;
  currentResourceId: string | number | null;
  sx?: React.CSSProperties;
  className?: string;
  isDisabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string;
  listLabel?: string;
  placeholder?: string;
  includeResourceId?: boolean; // ✅ NEW: Controls whether to include resource ID in display
  currentDomainId?: string | number | null; // ✅ NEW: Optional domain ID for cascading filter
  lazyLoad?: boolean; // ✅ NEW: Controls whether to fetch data on mount or on dropdown open
  filterByScoreConfigAssociated?: boolean; // ✅ NEW: When true, fetch only resources without an associated score config
}

/**
 * All resources list with auto complete dropdown functionality
 * @returns
 * Selected resource with id and name
 */
const AutoCompleteResourceList: React.FC<IAutoCompleteResourceList> = ({
  setIsLoading,
  handelOnChangeResource,
  currentResourceId,
  sx,
  className,
  isDisabled = false,
  required,
  error,
  helperText,
  listLabel,
  placeholder,
  includeResourceId = false, // ✅ NEW: Default to false to match established pattern
  currentDomainId, // ✅ NEW: Optional domain ID for cascading filter
  lazyLoad = false, // ✅ NEW: Default to false for backward compatibility
  filterByScoreConfigAssociated = false, // ✅ NEW: Default to false for backward compatibility
}) => {
  // Track if dropdown has been opened (for lazy loading)
  const [hasOpened, setHasOpened] = useState(false);

  // ✅ Pass currentDomainId to useFetchAllResources for domain-based filtering
  const [resourcesData, fetchResources, isFetching] = useFetchAllResources({
    setIsLoading,
    currentDomainId,
    fetchOnMount: !lazyLoad,
    isScoreConfigAssociated: filterByScoreConfigAssociated ? false : undefined,
  });

  // Handle dropdown open - trigger fetch if lazy loading
  const handleOpen = useCallback(() => {
    if (lazyLoad && !hasOpened) {
      setHasOpened(true);
      fetchResources();
    }
  }, [lazyLoad, hasOpened, fetchResources]);

  // When lazy-loading with a preselected id (e.g., restored from URL),
  // fetch immediately so the selected option can be resolved on first render.
  useEffect(() => {
    if (lazyLoad && !hasOpened && currentResourceId) {
      setHasOpened(true);
      fetchResources();
    }
  }, [lazyLoad, hasOpened, currentResourceId, fetchResources]);

  // Show loading spinner while fetching data
  const isLoading = lazyLoad && (isFetching || !hasOpened);

  return (
    <Autocomplete
      sx={sx}
      defaultValue={
        currentResourceId
          ? (resourcesData || []).find(
              (option) => option.id === currentResourceId,
            )
          : null
      }
      options={resourcesData || []}
      value={
        (resourcesData || []).find(
          (option) => option.id === currentResourceId,
        ) || null
      }
      getOptionLabel={(option) => {
        return includeResourceId
          ? `(${option.id}) ${option.resource_name}` || ""
          : option.resource_name || "";
      }}
      onOpen={handleOpen}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          style={{ color: "#000000" }}
          {...params}
          label={
            <span>
              {listLabel ? listLabel : "Resource"}
              {required ? <span className="required-asterisk">*</span> : ""}
            </span>
          }
          // required={required}
          placeholder={placeholder ? placeholder : "Select..."}
          InputLabelProps={{
            shrink: true,
          }}
          error={!!error}
          helperText={helperText || ""}
        />
      )}
      className={className}
      loadingText="Loading..."
      onChange={(event, value) => handelOnChangeResource(event, value)}
      disabled={Boolean(isDisabled)}
    />
  );
};

export default AutoCompleteResourceList;
